import { Package, Zap, Workflow, RefreshCw, Database } from "lucide-react";
import { useState } from "react";

const FeaturesShowcase = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: Package,
      title: "Real-Time Inventory Management",
      description: "Eliminate stockouts and overstock with unified inventory visibility across all channels. Real-time synchronization ensures accurate stock levels and optimal fulfillment decisions.",
      stat: "99.9%",
      statLabel: "Accuracy Rate",
    },
    {
      icon: Zap,
      title: "Intelligent Order Routing System",
      description: "Automatically route orders to the most efficient fulfillment location. Reduce shipping costs by 30% while improving delivery speed and customer satisfaction.",
      stat: "30%",
      statLabel: "Cost Reduction",
    },
    {
      icon: Workflow,
      title: "Visual Workflow Builder Platform",
      description: "Connect your systems without coding using our drag-and-drop interface. Automate complex processes and scale operations with confidence and ease.",
      stat: "10min",
      statLabel: "Setup Time",
    },
    {
      icon: RefreshCw,
      title: "Seamless Multi-Channel Sync",
      description: "Keep product data, inventory, and orders perfectly synchronized across all sales channels. Real-time updates eliminate data silos and ensure consistency.",
      stat: "Real-time",
      statLabel: "Data Sync",
    },
    {
      icon: Database,
      title: "Centralized Product Information Management",
      description: "Manage all product data from a single source of truth. Enrich product information, manage variants, and distribute accurate content across all channels effortlessly.",
      stat: "1000+",
      statLabel: "Attributes",
    },
  ];

  return (
    <section className="py-24 px-4 relative overflow-hidden pb-12" aria-labelledby="features-heading">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" aria-hidden="true" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[800px] md:h-[800px] bg-primary/10 rounded-full blur-3xl" aria-hidden="true" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <header className="text-center mb-20 animate-fade-in">
          <h2 id="features-heading" className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/60 bg-clip-text text-transparent">
            Powerful Features for Modern Commerce
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Everything you need to streamline operations and scale your business
          </p>
        </header>

        {/* Main Feature Display */}
        <article className="mb-16">
          <div className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-transparent rounded-2xl md:rounded-3xl p-6 md:p-12 backdrop-blur-sm border border-primary/20 animate-fade-in">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <div className="inline-flex items-center gap-3 mb-6">
                  <div className="p-4 rounded-2xl bg-primary/20 backdrop-blur-sm" aria-hidden="true">
                    {(() => {
                      const Icon = features[activeFeature].icon;
                      return <Icon className="h-8 w-8 text-primary" aria-hidden="true" />;
                    })()}
                  </div>
                  <span className="text-sm font-semibold text-primary uppercase tracking-wider" role="status" aria-live="polite">
                    Feature {activeFeature + 1} of {features.length}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 leading-tight">
                  {features[activeFeature].title}
                </h3>
                <p className="text-base md:text-lg text-muted-foreground mb-8 leading-relaxed">
                  {features[activeFeature].description}
                </p>
              </div>
              
              <aside className="lg:w-64 text-center" aria-label="Feature statistics">
                <p className="text-5xl md:text-6xl lg:text-7xl font-bold text-primary mb-3 animate-scale-in" aria-label={`${features[activeFeature].stat} ${features[activeFeature].statLabel}`}>
                  {features[activeFeature].stat}
                </p>
                <p className="text-lg text-muted-foreground font-medium">
                  {features[activeFeature].statLabel}
                </p>
              </aside>
            </div>
          </div>
        </article>

        {/* Feature Navigation */}
        <nav className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" aria-label="Feature selector">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <button
                key={index}
                onClick={() => setActiveFeature(index)}
                aria-label={`View ${feature.title}`}
                aria-pressed={activeFeature === index}
                className={`group relative p-6 rounded-2xl transition-all duration-300 ${
                  activeFeature === index
                    ? "bg-gradient-to-br from-primary to-primary/80 shadow-xl scale-105"
                    : "bg-card/50 hover:bg-card hover:shadow-lg hover-scale"
                }`}
              >
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className={`p-3 rounded-xl transition-colors ${
                    activeFeature === index 
                      ? "bg-white/20" 
                      : "bg-primary/10 group-hover:bg-primary/20"
                  }`} aria-hidden="true">
                    <Icon className={`h-6 w-6 ${
                      activeFeature === index ? "text-white" : "text-primary"
                    }`} aria-hidden="true" />
                  </div>
                  <span className={`text-sm font-semibold leading-tight ${
                    activeFeature === index ? "text-white" : "text-foreground"
                  }`}>
                    {feature.title.split(' ').slice(0, 2).join(' ')}
                  </span>
                </div>
                
                {/* Active Indicator */}
                {activeFeature === index && (
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-white rounded-full" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </section>
  );
};

export default FeaturesShowcase;
